import React, { useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Select, TextField, Typography } from "@mui/material";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import { testTemplateApi, errorMessage } from "./api";
import { CHANNEL_LABELS, channelsOf, usedVariables } from "./variables";
import VariableInputs from "./VariableInputs";
import { useFeedback } from "./common";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE = /^(\+?91)?[6-9]\d{9}$/;

// Sends one channel's finished message of a saved template to one address / number, to check it for real.
export default function TestMailDialog({ template, systemVariables = [], onClose }) {
  const feedback = useFeedback();
  const channels = channelsOf(template);
  const [channel, setChannel] = useState(channels.includes("email") ? "email" : channels[0]);
  const [to, setTo] = useState("");
  const [candidateid, setCandidateid] = useState("");
  const [courseid, setCourseid] = useState("");
  const [values, setValues] = useState({});
  const [filevalues, setFilevalues] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const used = usedVariables(template, channel, systemVariables);
  const byLabel = (list) => list.map((v) => ({ ...v, name: v.label }));
  const isEmail = channel === "email";

  const send = async () => {
    const target = to.trim().replace(/\s/g, "");
    if (isEmail ? !EMAIL.test(target) : !MOBILE.test(target)) return setError(isEmail ? "Enter a valid email address" : "Enter a 10-digit mobile number");
    if ((candidateid || courseid) && (!/^\d+$/.test(candidateid) || !/^\d+$/.test(courseid))) return setError("Give both Candidate ID and Course ID as numbers");
    setBusy(true);
    setError("");
    try {
      const res = await testTemplateApi(template.templateid, {
        channel, to: target, values, filevalues,
        ...(candidateid ? { candidateid: Number(candidateid), courseid: Number(courseid) } : {}),
      });
      feedback.success(res.message || "Test sent");
      onClose();
    } catch (err) {
      setError(errorMessage(err, "Could not send the test"));
    }
    setBusy(false);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send a Test — {template.eventname}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Select fullWidth size="small" value={channel} onChange={(e) => { setChannel(e.target.value); setTo(""); }}>
              {channels.map((c) => <MenuItem key={c} value={c}>{CHANNEL_LABELS[c]}</MenuItem>)}
            </Select>
          </Grid>
          <Grid item xs={8}>
            <TextField size="small" fullWidth label={isEmail ? "Send to (email) *" : "Send to (mobile) *"} placeholder={isEmail ? "you@example.com" : "9876543210"} value={to} onChange={(e) => setTo(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ color: "#555" }}>Optional: a Candidate ID and Course ID fill the values from the database.</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Candidate ID" value={candidateid} onChange={(e) => setCandidateid(e.target.value.trim())} />
          </Grid>
          <Grid item xs={6}>
            <TextField size="small" fullWidth label="Course ID" value={courseid} onChange={(e) => setCourseid(e.target.value.trim())} />
          </Grid>
          <Grid item xs={12}>
            <VariableInputs title={used.some((v) => v.source === "send") ? "Values typed when sending" : ""} variables={byLabel(used.filter((v) => v.source === "send"))} values={values} onChange={(n, v) => setValues({ ...values, [n]: v })} />
          </Grid>
          <Grid item xs={12}>
            <VariableInputs title={used.some((v) => v.source === "file") ? "Values from the upload file" : ""} variables={byLabel(used.filter((v) => v.source === "file"))} values={filevalues} onChange={(n, v) => setFilevalues({ ...filevalues, [n]: v })} />
          </Grid>
          {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button variant="outlined" size="small" onClick={onClose}>Cancel</Button>
        <BluePrimaryButton title={busy ? "Sending..." : `Send Test ${CHANNEL_LABELS[channel]}`} handleSubmit={send} disabled={busy} />
      </DialogActions>
    </Dialog>
  );
}
